import React from 'react';
import { cn } from '@/lib/utils';

type LiveStatus = 'connected' | 'connecting' | 'reconnecting';

interface LiveIndicatorProps {
  isConnected?: boolean;
  status?: LiveStatus;
  className?: string;
}

export function LiveIndicator({ isConnected, status, className }: LiveIndicatorProps) {
  const derivedStatus: LiveStatus = (() => {
    if (status) return status;
    return isConnected ? 'connected' : 'reconnecting';
  })();

  const tone = (() => {
    switch (derivedStatus) {
      case 'connected':
        return {
          dot: 'bg-emerald-500',
          label: 'text-emerald-700',
          text: 'Live',
          aria: 'Live connection established',
        };
      case 'connecting':
        return {
          dot: 'bg-amber-400',
          label: 'text-amber-700',
          text: 'Connecting…',
          aria: 'Attempting to connect',
        };
      case 'reconnecting':
      default:
        return {
          dot: 'bg-rose-500',
          label: 'text-rose-700',
          text: 'Reconnecting…',
          aria: 'Connection lost, attempting to reconnect',
        };
    }
  })();

  return (
    <div
      className={cn('live-indicator inline-flex items-center gap-2 text-sm', className)}
      role="status"
      aria-live="polite"
      aria-label={tone.aria}
      title={tone.aria}
    >
      <span
        className={cn('inline-block h-2.5 w-2.5 rounded-full transition-colors duration-150', tone.dot)}
        aria-hidden="true"
      />
      <span className={cn('font-medium transition-colors duration-150', tone.label)}>
        {tone.text}
      </span>
    </div>
  );
}
